import { Highlight, ThoughtLeadership, AdvancingOrg, GrowthArea } from '../types';
import { getIdToken } from '../auth-config';

// Use relative paths in production (empty string) or localhost for development
const API_BASE_URL =
  process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://127.0.0.1:8001');

class ApiService {
  private getCurrentUserAlias(): string | null {
    // This will be set by the React component that uses the API
    const alias = (window as any).__currentUserAlias;
    return alias || null;
  }

  // Method to set user alias directly (alternative to window variable)
  setCurrentUserAlias(alias: string) {
    (window as any).__currentUserAlias = alias;
  }

  // TODO: Replace global window variable with React Context for better security

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    // Get user info from OIDC context if available
    const userAlias = this.getCurrentUserAlias();

    // Get ID token for API Gateway Cognito Authorizer
    const idToken = getIdToken();

    const headers = {
      'Content-Type': 'application/json',
      ...(userAlias && { 'x-user-alias': userAlias }),
      ...(idToken && { Authorization: `Bearer ${idToken}` }),
      ...options?.headers,
    };

    try {
      const response = await fetch(url, {
        headers,
        ...options,
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      // Handle empty responses (e.g., DELETE requests with 204 No Content)
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // Return empty object for void responses
        return {} as T;
      }

      const text = await response.text();
      if (!text) {
        return {} as T;
      }

      const data = JSON.parse(text);
      return data;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  // Highlights
  async getHighlights(directReportAlias?: string): Promise<Highlight[]> {
    const url = directReportAlias
      ? `/api/v1/highlights?direct_report_alias=${directReportAlias}`
      : `/api/v1/highlights`;
    return this.request<Highlight[]>(url);
  }

  async getHighlight(id: string): Promise<Highlight> {
    return this.request<Highlight>(`/api/v1/highlights/${id}`);
  }

  async createHighlight(highlight: Omit<Highlight, 'id'>, directReportAlias?: string): Promise<Highlight> {
    const url = directReportAlias
      ? `/api/v1/highlights?direct_report_alias=${directReportAlias}`
      : `/api/v1/highlights`;
    return this.request<Highlight>(url, {
      method: 'POST',
      body: JSON.stringify(highlight),
    });
  }

  async updateHighlight(id: string, highlight: Omit<Highlight, 'id'>): Promise<Highlight> {
    return this.request<Highlight>(`/api/v1/highlights/${id}`, {
      method: 'PUT',
      body: JSON.stringify(highlight),
    });
  }

  async deleteHighlight(id: string): Promise<void> {
    await this.request(`/api/v1/highlights/${id}`, {
      method: 'DELETE',
    });
  }

  // Thought Leadership
  async getThoughtLeadership(directReportAlias?: string): Promise<ThoughtLeadership[]> {
    const url = directReportAlias
      ? `/api/v1/thought-leadership?direct_report_alias=${directReportAlias}`
      : `/api/v1/thought-leadership`;
    return this.request<ThoughtLeadership[]>(url);
  }

  async getThoughtLeadershipItem(id: string): Promise<ThoughtLeadership> {
    return this.request<ThoughtLeadership>(`/api/v1/thought-leadership/${id}`);
  }

  async createThoughtLeadership(
    item: Omit<ThoughtLeadership, 'id'>,
    directReportAlias?: string,
  ): Promise<ThoughtLeadership> {
    const url = directReportAlias
      ? `/api/v1/thought-leadership?direct_report_alias=${directReportAlias}`
      : `/api/v1/thought-leadership`;
    return this.request<ThoughtLeadership>(url, {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async updateThoughtLeadership(id: string, item: Omit<ThoughtLeadership, 'id'>): Promise<ThoughtLeadership> {
    return this.request<ThoughtLeadership>(`/api/v1/thought-leadership/${id}`, {
      method: 'PUT',
      body: JSON.stringify(item),
    });
  }

  async deleteThoughtLeadership(id: string): Promise<void> {
    await this.request(`/api/v1/thought-leadership/${id}`, {
      method: 'DELETE',
    });
  }

  // Advancing Org
  async getAdvancingOrg(directReportAlias?: string): Promise<AdvancingOrg[]> {
    const url = directReportAlias
      ? `/api/v1/advancing-org?direct_report_alias=${directReportAlias}`
      : `/api/v1/advancing-org`;
    return this.request<AdvancingOrg[]>(url);
  }

  async getAdvancingOrgItem(id: string): Promise<AdvancingOrg> {
    return this.request<AdvancingOrg>(`/api/v1/advancing-org/${id}`);
  }

  async createAdvancingOrg(item: Omit<AdvancingOrg, 'id'>, directReportAlias?: string): Promise<AdvancingOrg> {
    const url = directReportAlias
      ? `/api/v1/advancing-org?direct_report_alias=${directReportAlias}`
      : `/api/v1/advancing-org`;
    return this.request<AdvancingOrg>(url, {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async updateAdvancingOrg(id: string, item: Omit<AdvancingOrg, 'id'>): Promise<AdvancingOrg> {
    return this.request<AdvancingOrg>(`/api/v1/advancing-org/${id}`, {
      method: 'PUT',
      body: JSON.stringify(item),
    });
  }

  async deleteAdvancingOrg(id: string): Promise<void> {
    await this.request(`/api/v1/advancing-org/${id}`, {
      method: 'DELETE',
    });
  }

  // Manager approval methods
  async updateHighlightManagerApproval(id: string, approved: boolean): Promise<void> {
    await this.request(`/api/v1/highlights/${id}/manager-approval?approved=${approved}`, {
      method: 'PATCH',
    });
  }

  async updateThoughtLeadershipManagerApproval(id: string, approved: boolean): Promise<void> {
    await this.request(`/api/v1/thought-leadership/${id}/manager-approval?approved=${approved}`, {
      method: 'PATCH',
    });
  }

  async updateAdvancingOrgManagerApproval(id: string, approved: boolean): Promise<void> {
    await this.request(`/api/v1/advancing-org/${id}/manager-approval?approved=${approved}`, {
      method: 'PATCH',
    });
  }

  // Growth Areas
  async getGrowthAreas(directReportAlias?: string): Promise<GrowthArea[]> {
    const url = directReportAlias
      ? `/api/v1/growth-areas?direct_report_alias=${directReportAlias}`
      : `/api/v1/growth-areas`;
    return this.request<GrowthArea[]>(url);
  }

  async getGrowthAreaItem(id: string): Promise<GrowthArea> {
    return this.request<GrowthArea>(`/api/v1/growth-areas/${id}`);
  }

  async createGrowthArea(item: Omit<GrowthArea, 'id'>, directReportAlias?: string): Promise<GrowthArea> {
    const url = directReportAlias
      ? `/api/v1/growth-areas?direct_report_alias=${directReportAlias}`
      : `/api/v1/growth-areas`;
    return this.request<GrowthArea>(url, {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async updateGrowthArea(id: string, item: Omit<GrowthArea, 'id'>): Promise<GrowthArea> {
    return this.request<GrowthArea>(`/api/v1/growth-areas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(item),
    });
  }

  async deleteGrowthArea(id: string): Promise<void> {
    await this.request(`/api/v1/growth-areas/${id}`, {
      method: 'DELETE',
    });
  }

  async updateGrowthAreaManagerApproval(id: string, approved: boolean): Promise<void> {
    await this.request(`/api/v1/growth-areas/${id}/manager-approval?approved=${approved}`, {
      method: 'PATCH',
    });
  }

  // Direct Reports
  async getDirectReports(): Promise<any> {
    return this.request<any>(`/api/v1/direct-reports`);
  }

  // AI Summarization - uses separate Lambda endpoint
  async getAiStatus(): Promise<{ ai_enabled: boolean }> {
    return this.request<{ ai_enabled: boolean }>('/api/v1/ai/status');
  }

  async summarizeText(
    text: string,
    contextType: 'highlight' | 'thought_leadership' | 'advancing_org' = 'highlight',
  ): Promise<{ summary: string; feedback?: string }> {
    return this.request<{ summary: string; feedback?: string }>('/api/v1/ai/summarize', {
      method: 'POST',
      body: JSON.stringify({
        text,
        context_type: contextType,
      }),
    });
  }
}

export const api = new ApiService();
